import {createSlice} from '@reduxjs/toolkit';
import {permissionApi} from '../../../../api/permission/api';
import {getErrorMessage} from '../../../utils/requests/error';

export const rolesSlice = createSlice({
    name: 'roles',
    initialState: {
        roles: {},
        isRolesFetching: false,
        rolesFetchingError: null,
        isRoleSaving: false,
        roleSavingError: null
    },
    reducers: {
        setRoles: (state, action) => {
            state.roles = action.payload;
        },
        setIsRolesFetching: (state, action) => {
            state.isRolesFetching = action.payload;
        },
        setRolesFetchingError: (state, action) => {
            state.rolesFetchingError = action.payload;
        },
        setIsRoleSaving: (state, action) => {
            state.isRoleSaving = action.payload;
        },
        setRoleSavingError: (state, action) => {
            state.roleSavingError = action.payload;
        }
    }
});

export const {
    setRoles,
    setIsRolesFetching,
    setRolesFetchingError,
    setIsRoleSaving,
    setRoleSavingError
} = rolesSlice.actions;

export const fetchRoles = () => async (dispatch, getState) => {
    const {activeAccountId} = getState().user;
    dispatch(setIsRolesFetching(true));
    dispatch(setRolesFetchingError(null));

    try {
        const result = await permissionApi.getAllRoles(activeAccountId);
        dispatch(setRoles(result));
        dispatch(setIsRolesFetching(false));
        return true;
    } catch (err) {
        dispatch(setIsRolesFetching(false));
        dispatch(setRolesFetchingError(getErrorMessage(err)));
        return false;
    }
};

export const saveRole = (role = {}) => async (dispatch, getState) => {
    const {activeAccountId} = getState().user;
    dispatch(setIsRoleSaving(true));
    dispatch(setRoleSavingError(null));

    const {description, ...rest} = role;
    const payload = description ? {...rest, description} : rest;

    try {
        if (payload.id) {
            await permissionApi.updateRole(activeAccountId, payload.id, payload);
        } else {
            await permissionApi.createRole(activeAccountId, payload);
        }

        dispatch(fetchRoles());
        dispatch(setIsRoleSaving(false));
        return true;
    } catch (err) {
        dispatch(setIsRoleSaving(false));
        dispatch(setRoleSavingError(getErrorMessage(err)));
        return false;
    }
};

export const toggleRoles = (rolesIds = [], enabled) => async (dispatch, getState) => {
    const {activeAccountId} = getState().user;
    dispatch(setIsRoleSaving(true));
    dispatch(setRoleSavingError(null));

    try {
        await Promise.all(
            rolesIds.map((id) => permissionApi.updateRole(activeAccountId, id, {enabled}))
        );

        dispatch(fetchRoles());
        dispatch(setIsRoleSaving(false));
        return true;
    } catch (err) {
        dispatch(setIsRoleSaving(false));
        dispatch(setRoleSavingError(getErrorMessage(err)));
        return false;
    }
};

export const deleteRoles = (rolesIds = []) => async (dispatch, getState) => {
    const {activeAccountId} = getState().user;
    dispatch(setIsRoleSaving(true));
    dispatch(setRoleSavingError(null));

    try {
        await Promise.all(
            rolesIds.map((id) => permissionApi.deleteRole(activeAccountId, id))
        );

        dispatch(fetchRoles());
        dispatch(setIsRoleSaving(false));
        return true;
    } catch (err) {
        dispatch(setIsRoleSaving(false));
        dispatch(setRoleSavingError(getErrorMessage(err)));
        return false;
    }
};

export const selectRoles = (state) => state.roles.roles;
export const selectIsRolesFetching = (state) => state.roles.isRolesFetching;
export const selectRolesFetchingError = (state) => state.roles.rolesFetchingError;
export const selectIsRoleSaving = (state) => state.roles.isRoleSaving;
export const selectRoleSavingError = (state) => state.roles.roleSavingError;

export default rolesSlice.reducer;
